/*
 * File: OverviewStep.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 04 Jan 2022 3:54:05 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { Button, Col, Row, Space, Typography } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { InfoPanel } from 'components/molecules/InfoPanel';
import { PostedAttachmentsPanel } from 'components/molecules/PostedAttachmentsPanel';
import { PostedSalesOrderProductsTable } from 'components/molecules/PostedSalesOrderProductsTable';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';
import { useGetSalesOrderQuery } from 'redux/services/useMamaBearService';

const styles: InlineStylesModel = {
  mainContainer: {
    overflowY: 'auto',
    overflowX: 'hidden',
    margin: 0,
    width: '100%'
  },
  headerTitle: {
    fontWeight: 'bold',
    color: '#2F5596'
  },
  cardsContainer: {
    padding: '0px',
    margin: '0px'
  }
};

export const PostedSalesOrder = (): JSX.Element => {
  const { selectedCompanyDataAreaId } = useSelector((state: ReduxState) => state.salesOrders);

  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useGetSalesOrderQuery({ id: id as string }, { skip: !id });



  /* ****************** Functions ****************** */
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  });
  const numberFormatter = new Intl.NumberFormat('en-US', {
    style: 'decimal'
  });

  /* ****************** JSX  / Variables ****************** */
  const margin = data?.lines.map((item) => Number(item.quantity) * Number(item.unitPrice) - Number(item.quantity) * Number(item.unitCost)).reduce((a, b) => a + b, 0);
  const totalQty = data?.lines.map((item) => Number(item.quantity)).reduce((a, b) => a + b, 0);
  const totalPrice = data?.lines.map((item) => Number(item.unitPrice) * Number(item.quantity)).reduce((a, b) => a + b, 0);



  if (isLoading)
    return (
      <Row style={{ marginTop: 50 }}>
        <BlockLoader direction="loader loader--slideUp" />
      </Row>
    );

  /* ****************** Render ****************** */
  return (
    <Row style={styles.mainContainer} gutter={[0, 16]}>
      <Row justify="space-between" align="middle" style={{ width: '100%' }}>
        <Space>
          {data?.customerPo && (
            <Space>
              <Typography.Text style={styles.headerTitle}>SO Number: </Typography.Text>
              <Typography.Link
                    onClick={(e): void => e.stopPropagation()}
                    style={{ zIndex: 9999 }}
                    href={`${process.env.REACT_APP_LITTLE_MURRAY_SPARKLES_URL}/${selectedCompanyDataAreaId}/${data.customerId}/orderDetails/${data.salesOrderNumber}`}
                    target="_blank">
                    {data.salesOrderNumber}
                  </Typography.Link>
            </Space>
          )}
          {data?.customerPo && (
            <Space>
              <Typography.Text style={styles.headerTitle}>Customer Name: </Typography.Text>
              <Typography.Text>{data?.customerName}</Typography.Text>
            </Space>
          )}
          {data?.customerPo && (
            <Space>
              <Typography.Text style={styles.headerTitle}>PO Number: </Typography.Text>
              <Typography.Text>{data?.customerPo}</Typography.Text>
            </Space>
          )}
        </Space>
        <Row gutter={[8, 8]}>
          <Col>
            <Button onClick={(): void => navigate('/')}>Back to Search</Button>
          </Col>
        </Row>
      </Row>
      <Col span={24} style={styles.cardsContainer}>
        <Row gutter={[16, 8]}>
          <Col lg={24} xl={24} style={{ width: '100%' }}>
            <InfoPanel
              title="Header Details"
              headerColor="rgb(206, 213, 242)"
              items={[
                { label: 'Sales Person', value: data?.salesPersonFullName },
                { label: 'Sales Support', value: data?.salesSupportFullName },
                { label: 'Buyer', value: data?.buyerName },
                { label: 'Sales Order Type', value: data?.salesOrderType },
                { label: 'Site', value: data?.siteId },
                { label: 'Warehouse', value: data?.warehouseId },
                { label: 'Quote Id', value: data?.quoteId },
                { label: 'Lab Configuration Required', value: data?.labConfiguration.toString().toUpperCase() },
                { label: 'Ship Complete', value: data?.shipComplete ? 'Completed' : 'Incomplete' }
              ]}
            />
          </Col>
          <Col lg={24} xl={24} style={{ width: '100%' }}>
            <InfoPanel
              title="Delivery Information"
              headerColor="rgba(24, 167, 153, 0.518)"
              items={[
                { label: 'Name', value: data?.deliveryContactName },
                { label: 'Phone', value: data?.deliveryContactPhone },
                { label: 'Address', value: data?.deliveryAddressFormatted },
                { label: 'Freight Terms', value: data?.freightTerms },
                { label: 'Mode of Delivery', value: data?.modeOfDelivery },
                { label: 'Requested Ship Date', value: new Date(data?.requestedShipDate as string).toLocaleDateString() }
              ]}
            />
          </Col>
          <Col lg={24} xl={24} style={{ width: '100%' }}>
            <PostedSalesOrderProductsTable data={data} />
          </Col>
          <Col lg={24} xl={24} style={{ width: '100%' }}>
            <PostedAttachmentsPanel data={data} />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
